import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Switch, useLocation } from 'react-router';
import OnboardingPage from '@features/auth/pages/onboarding/Onboarding';
import PrivateRoute from './PrivateRoute';
import ManagementRouting from '@features/settings/pages/ManagementRouting';
import { selectCurrentUser, selectIsAuthLoading } from '@shared/store/auth.duck';
import { getParameterByName, updateLastPath } from '@shared/utils/UrlUtils';
import { DialogIdentifiers, openDialog } from '@shared/store/dialog.duck';
import { LoadConfig } from '@shared/hooks/LoadConfig';
import { Route } from "react-router-dom";
import WallPlannerRouting from "@features/settings/pages/WallPlannerRouting";
import { LoadingSpinner } from "@shared/ui/atoms/LoadingSpinner";
import { WithMsTeamsAppStoreRouting } from '@shared/hooks/WithMsTeamsAppStoreRouting';
import { FloorPlanEditPage } from "@features/desks/pages/FloorPlanEditPage";
import { MeetingRoomRouter } from "@features/meeting-rooms";
import WhereaboutsRouting from "@features/whereabouts/pages/team-movements/WhereaboutsRouting";
import { ManageUsersPage } from "@features/settings/pages/manage-users-page-v2/ManageUsersPage";
import TagManagementPage from "@features/settings/pages/tag-management-page/TagManagementPage";
import { ShowOnboarding } from '@shared/hooks/ShowOnboarding';
import { AddUsersPage } from "@features/settings/pages/manage-users-page-v2/AddUsersPage";
import { DeletedUsersPage } from "@features/settings/pages/deleted-users-page/DeletedUsersPage";
import HolidaysRouting from "@features/holidays/pages/HolidaysRouting";

export default function SecureApp() {
  document.body.style.backgroundColor = 'white';
  const dispatch = useDispatch();
  const location = useLocation();
  const current = useSelector(selectCurrentUser);
  const loading = useSelector(selectIsAuthLoading);
  const paymentState = useMemo(() => getParameterByName('state', location.search), [location]);
  const usersWhereaboutsEdited = useMemo(() => current?.whereaboutsEdited, [current]);

  const showOnboarding = ShowOnboarding();
  LoadConfig();
  WithMsTeamsAppStoreRouting();

  useEffect(() => {
    if (usersWhereaboutsEdited) {
      dispatch(openDialog(DialogIdentifiers.WhereaboutsModifiedDialog));
    }
  }, [dispatch, usersWhereaboutsEdited]);

  useEffect(() => {
    if (current?.firstTime) {
      dispatch(openDialog(DialogIdentifiers.WelcomeDialog));
    }
  }, [dispatch, current?.firstTime]);

  useEffect(() => {
    if (paymentState === 'canceled') dispatch(openDialog(DialogIdentifiers.StripeFailure));
    if (paymentState === 'success') dispatch(openDialog(DialogIdentifiers.StripeSuccess));
  }, [paymentState, dispatch]);

  useEffect(() => {
    updateLastPath(location.pathname);
  }, [location]);


  if (loading || !current) {
    return <LoadingSpinner fullScreen={true} message={"Secure app loading"}/>
  }

  if (showOnboarding && !location.pathname.includes('/onboarding')) {
    return (<Redirect to="/onboarding"/>)
  }

  return (
    <>
      <Switch>
        <PrivateRoute exact path="/onboarding" component={OnboardingPage}/>

        <Route path="/whereabouts">
          <WhereaboutsRouting/>
        </Route>

        <Route path="/holidays">
          <HolidaysRouting/>
        </Route>

        <Route path="/work-planner">
          <WallPlannerRouting/>
        </Route>

        {/* Header and footers are managed internally*/}
        <PrivateRoute exact path="/manage/users" component={ManageUsersPage} />
        <PrivateRoute exact path="/manage/deleted-users" component={DeletedUsersPage} />
        <PrivateRoute exact path="/manage/add-users" component={AddUsersPage} />
        <PrivateRoute exact path="/manage/workgroups" component={TagManagementPage} />

        <Route path="/manage">
          <ManagementRouting/>
        </Route>

        <PrivateRoute exact path="/edit-floor-plan" component={FloorPlanEditPage} />


        <Route path="/meeting-rooms">
          <MeetingRoomRouter/>
        </Route>

      </Switch>
    </>
  )
}

